import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import { 
  Save, Copy, ChevronLeft, ChevronRight, 
  MapPin, Mountain, Trees, Waves, Sun 
} from 'lucide-react'
import { LocationResultTabs } from './LocationResultTabs'
import { DangerIndicator } from './DangerIndicator'
import type { LocationResult as LocationResultType } from '@creanizer/types'

interface LocationResultProps {
  result: LocationResultType
  batchResults?: LocationResultType[] | null
  onSave: () => void
  isSaved: boolean
  isStreaming?: boolean
  streamingText?: string
}

export function LocationResult({ 
  result, 
  batchResults, 
  onSave, 
  isSaved,
  isStreaming,
  streamingText
}: LocationResultProps) {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const currentResult = batchResults ? batchResults[selectedIndex] : result

  const handleCopy = () => {
    const textContent = formatLocationAsText(currentResult)
    navigator.clipboard.writeText(textContent)
  }

  const handleNext = () => {
    if (batchResults && selectedIndex < batchResults.length - 1) {
      setSelectedIndex(selectedIndex + 1)
    }
  }

  const handlePrevious = () => {
    if (batchResults && selectedIndex > 0) {
      setSelectedIndex(selectedIndex - 1)
    }
  }

  if (isStreaming && streamingText) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>生成中...</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="whitespace-pre-wrap font-mono text-sm">
            {streamingText}
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      {batchResults && batchResults.length > 1 && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePrevious}
                  disabled={selectedIndex === 0}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm">
                  {selectedIndex + 1} / {batchResults.length}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNext}
                  disabled={selectedIndex === batchResults.length - 1}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
              <Select
                value={selectedIndex.toString()}
                onValueChange={(v) => setSelectedIndex(parseInt(v))}
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {batchResults.map((loc, index) => (
                    <SelectItem key={index} value={index.toString()}>
                      {loc.basicInfo.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <CardTitle className="text-2xl flex items-center gap-2">
                <MapPin className="h-6 w-6" />
                {currentResult.basicInfo.name}
              </CardTitle>
              {currentResult.basicInfo.aliases && currentResult.basicInfo.aliases.length > 0 && (
                <p className="text-sm text-gray-600">
                  別名: {currentResult.basicInfo.aliases.join('、')}
                </p>
              )}
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-600">
                  {currentResult.basicInfo.type} / {currentResult.basicInfo.scale}
                </span>
                <DangerIndicator level={currentResult.basicInfo.accessibility?.difficulty || 5} />
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopy}
              >
                <Copy className="h-4 w-4 mr-2" />
                コピー
              </Button>
              <Button
                variant={isSaved ? "outline" : "default"}
                size="sm"
                onClick={onSave}
                disabled={isSaved}
              >
                <Save className="h-4 w-4 mr-2" />
                {isSaved ? '保存済み' : '保存'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <LocationResultTabs location={currentResult} />
        </CardContent>
      </Card>
    </div>
  )
}

function formatLocationAsText(location: LocationResultType): string {
  const sections = []
  
  sections.push(`【${location.basicInfo.name}】`)
  sections.push('')
  sections.push('## 基本情報')
  sections.push(`タイプ: ${location.basicInfo.type}`)
  sections.push(`規模: ${location.basicInfo.scale}`)
  sections.push(`用途: ${location.basicInfo.purpose}`)
  sections.push(`危険度: ${location.basicInfo.accessibility?.difficulty || 5}/10`)
  sections.push('')
  sections.push(location.basicInfo.description)
  sections.push('')
  sections.push('雰囲気・印象・意義:')
  sections.push(location.basicInfo.atmosphereAndSignificance)
  
  if (location.geography) {
    sections.push('')
    sections.push('## 地理・地形')
    sections.push(`主要地形: ${location.geography.terrain.primary}`)
    if (location.geography.terrain.elevation) {
      sections.push(`標高: ${location.geography.terrain.elevation}`)
    }
    if (location.geography.terrain.area) {
      sections.push(`面積: ${location.geography.terrain.area}`)
    }
    if (location.geography.features && location.geography.features.length > 0) {
      sections.push('')
      sections.push('### 特徴')
      location.geography.features.forEach(feature => {
        sections.push(`- ${feature.name} (${feature.type}): ${feature.description}`)
      })
    }
    if (location.geography.adjacentAreas) {
      sections.push('')
      sections.push('### 隣接地域')
      sections.push(location.geography.adjacentAreas)
    }
  }
  
  if (location.environment) {
    sections.push('')
    sections.push('## 環境・気候')
    sections.push(`気候: ${location.environment.climate.type}`)
    sections.push(`平均気温: ${location.environment.climate.averageTemperature}`)
    if (location.environment.climate.extremeWeather && location.environment.climate.extremeWeather.length > 0) {
      sections.push('')
      sections.push('極端な天候:')
      location.environment.climate.extremeWeather.forEach(weather => {
        sections.push(`- ${weather}`)
      })
    }
  }
  
  if (location.wildlife) {
    sections.push('')
    sections.push('## 野生動植物')
    if (location.wildlife.fauna.apex.length > 0) {
      sections.push('### 頂点捕食者')
      location.wildlife.fauna.apex.forEach(apex => {
        sections.push(`- ${apex.name}: ${apex.description}`)
      })
    }
  }
  
  if (location.history) {
    sections.push('')
    sections.push('## 歴史・伝承')
    if (location.history.origin) {
      sections.push(`形成: ${location.history.origin.formation}`)
      sections.push(`年代: ${location.history.origin.age}`)
    }
    if (location.history.legends && location.history.legends.length > 0) {
      sections.push('')
      sections.push('### 伝説')
      location.history.legends.forEach(legend => {
        sections.push(`「${legend.title}」`)
        sections.push(legend.story)
        sections.push('')
      })
    }
  }
  
  return sections.join('\n')
}